import { Checkbox, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import React, { useState } from 'react';
import componentConstants from '../../../core-utils/messages/componentConstants';
import useToggle from '../../../core-utils/custom-hooks/Toggle';
import {
  Eye,
  EyeOffOutline,
  Typography,
  TextField,
} from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { ProgressBar } from '@HomaHealth/ui-component-library';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { Button } from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import HOMA from '../../../../public/Images/Homa.png';
import InfoBar from '../../atoms/InfoBar';
import axios from 'axios';
import properties from '../../../core-utils/properties';
import { useNavigate } from 'react-router-dom';
import { LOGIN } from '../../../core-utils/routes';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import { isCareNavigator } from '../../../core-utils/helper-utils/helper';
import TermsModal from '../TermsModal';

interface PasswordConstraints {
  minCharacters: boolean;
  upperCaseLetter: boolean;
  lowerCaseLetter: boolean;
  aNumber: boolean;
  specialCharacter: boolean;
}

interface colorAndCompleted {
  color: string;
  completed: number;
}

export interface ResetPasswordOrganismProps {
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  isCreatePassword: boolean;
  userData: any;
  passwordError: string;
  setPasswordError: React.Dispatch<React.SetStateAction<string>>;
}

const ResetPasswordOrganism: React.FC<ResetPasswordOrganismProps> = ({
  setLoading,
  isCreatePassword,
  userData,
  passwordError,
  setPasswordError,
}) => {
  // constants
  const constants = componentConstants.ORGANISMS.RESET_PASSWORD;
  const iconArgs = {
    height: '24px',
    width: '24px',
    style: {
      cursor: 'pointer',
    },
  };
  const constraintTypographyProps = {
    fontSize: '12px',
    textColor: patientAccessTheme.palette.text.lowemphasis,
    lineHeight: '22px',
    margin: '0px 0px 4px 0px',
  };

  const useStyles = makeStyles()({
    root: {
      display: 'flex',
      flexDirection: 'column',
    },
    checkbox: {
      '&.MuiCheckbox-root': {
        padding: '0px',
      },

      '&:hover': {
        backgroundColor: 'transparent !important',
      },
      '& .MuiSvgIcon-root': {
        width: '24px',
        height: '24px',
      },
    },
    firstItem: {
      display: 'flex',
      background: '#EEF3FF',
      padding: '7px 20px 8px 15px',
      margin: '0px 0px 16px 0px',
      borderRadius: '4px',
      width: '372px',
    },
    text: {
      textDecoration: 'dashed',
      textDecorationLine: 'underline',
      textDecorationThickness: '0.5px',
      textUnderlineOffset: '5px',
      paddingLeft: '5px',
    },
  });
  const { classes } = useStyles();

  // state variables
  const [termsModal, setTermsModal] = useState<boolean>(false);
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>('');
  const [bothPassSame, setBothPassSame] = useState<boolean>(false);
  const [terms, setTerms] = useState<boolean>(false);
  const [showNewPassword, toggleNewPassword] = useToggle();
  const [showConfirmNewPassword, toggleConfirmNewPassword] = useToggle();
  const [progress, setProgress] = useState<colorAndCompleted>({
    color: patientAccessTheme.palette.warning.A400 ?? '',
    completed: 5,
  });
  const [
    passwordConstraintsError,
    setPasswordConstraintsError,
  ] = useState<PasswordConstraints>({
    aNumber: true,
    lowerCaseLetter: true,
    minCharacters: true,
    specialCharacter: true,
    upperCaseLetter: true,
  });
  const [
    allConstraintsSatisfied,
    setAllConstraintsSatisfied,
  ] = useState<boolean>(false);

  // stores and custom hooks
  const CryptoJS = require('crypto-js');
  const secret = process.env.ENCRYPTION_SECRET_KEY;
  const navigate = useNavigate();

  // functions
  const progressBarStages = (completed: number): colorAndCompleted => {
    switch (completed) {
      case 20:
        return {
          color: patientAccessTheme.palette.warning.A400 ?? '',
          completed: 25,
        };
      case 40:
        return {
          color: patientAccessTheme.palette.warning.A400 ?? '',
          completed: 50,
        };
      case 60:
        return {
          color: patientAccessTheme.palette.status.success ?? '',
          completed: 75,
        };
      case 80:
        return {
          color: patientAccessTheme.palette.status.success ?? '',
          completed: 75,
        };
      case 100:
        return {
          color: patientAccessTheme.palette.primary[500] ?? '',
          completed: 100,
        };
    }
    return {
      color: patientAccessTheme.palette.warning.A400 ?? '',
      completed: 5,
    };
  };

  const validatePassword = (password: string) => {
    let constraintWeight = 0;
    passwordConstraintsError.minCharacters = !(password.length >= 8);
    passwordConstraintsError.aNumber = !password.match(/\d/);
    passwordConstraintsError.lowerCaseLetter = !password.match(/[a-z]/g);
    passwordConstraintsError.upperCaseLetter = !password.match(/[A-Z]/g);
    passwordConstraintsError.specialCharacter = !password.match(/[@$!%*#?&]/g);

    Object.values(passwordConstraintsError).forEach((isError) => {
      if (!isError) {
        constraintWeight++;
      }
    });

    setProgress(progressBarStages(constraintWeight * 20)); // total 5 constraints, 5*20 = 100
    setAllConstraintsSatisfied(constraintWeight === 5);
    setPasswordConstraintsError({ ...passwordConstraintsError });
  };

  const handleLoginTextChange = (e: any, fieldName: 'new' | 'confirm') => {
    const value = e.target.value as string;
    setPasswordError('');
    validatePassword(value);
    if (fieldName === 'new') {
      setNewPassword(value);
    } else {
      setBothPassSame(newPassword === value);
      setConfirmNewPassword(value);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    const encryptedData = CryptoJS.AES.encrypt(
      JSON.stringify({ password: newPassword, id: userData?.userId }),
      secret,
    ).toString();

    const endpoint = isCareNavigator(userData?.role)
      ? properties.careNavigator
      : properties.users;

    await axios
      .patch(properties.userManagementBaseUrl + endpoint + '/reset-password', {
        encryptedData: encryptedData,
      })
      .then(() => {
        navigate(LOGIN, { state: { isCreatePassword } });
        setLoading(false);
      })
      .catch((error: any) => {
        const errorMessage =
          error?.response?.data?.error || 'Error while updating password';
        setPasswordError(errorMessage);
        setLoading(false);
      });
  };

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter' && allConstraintsSatisfied) {
      handleSubmit();
    }
  };

  return (
    <>
      <div onKeyDown={handleKeyDown}>
        <Grid
          container
          sx={{
            flexDirection: 'column',
          }}
        >
          <Grid style={{ marginBottom: '20px' }}>
            <img src={HOMA} alt="HOMA" />
          </Grid>
          {passwordError && (
            <InfoBar
              info={passwordError}
              type="Error"
              width={'372px'}
              height={'auto'}
            />
          )}
          <Grid style={{ marginBottom: '8px' }}>
            <Typography
              textColor={patientAccessTheme.palette.text.highemphasis}
              fontSize="24px"
              lineHeight="29px"
              fontType="semiBold"
            >
              {isCreatePassword
                ? constants.CREATE_TITLE
                : constants.RESET_TITLE}
            </Typography>
          </Grid>
          <Grid style={{ marginBottom: '35px' }}>
            <Typography
              textColor={patientAccessTheme.palette.text.mediumemphasis}
              fontSize="14px"
              lineHeight="25px"
            >
              {isCreatePassword
                ? constants.CREATE_SUBTITLE
                : constants.RESET_SUBTITLE}
            </Typography>
          </Grid>
          <Grid style={{ marginBottom: '18px' }}>
            <TextField
              width="372px"
              height="46px"
              placeholder={constants.ENTER_NEW_PASSWORD}
              type={showNewPassword ? 'text' : 'password'}
              endAdornment={
                showNewPassword ? (
                  <Eye {...iconArgs} onClick={toggleNewPassword} />
                ) : (
                  <EyeOffOutline {...iconArgs} onClick={toggleNewPassword} />
                )
              }
              onChange={(e: any) => handleLoginTextChange(e, 'new')}
              value={newPassword}
            />
          </Grid>
          <Grid style={{ marginBottom: '18px' }}>
            <TextField
              width="372px"
              height="46px"
              placeholder={constants.CONFIRM_NEW_PASSWORD}
              type={showConfirmNewPassword ? 'text' : 'password'}
              endAdornment={
                showConfirmNewPassword ? (
                  <Eye {...iconArgs} onClick={toggleConfirmNewPassword} />
                ) : (
                  <EyeOffOutline
                    {...iconArgs}
                    onClick={toggleConfirmNewPassword}
                  />
                )
              }
              onChange={(e: any) => handleLoginTextChange(e, 'confirm')}
              value={confirmNewPassword}
            />
          </Grid>
          <Grid style={{ marginBottom: '12px' }}>
            <ProgressBar
              colorAndCompleted={[progress]}
              height={'5px'}
              width="372px"
              backgroundColor={patientAccessTheme.palette.grey[300]}
            />
          </Grid>
          <Grid
            container
            sx={{
              flexDirection: 'column',
            }}
          >
            <Typography {...constraintTypographyProps}>
              {!allConstraintsSatisfied
                ? constants.CONSTRAINTS_INFO
                : !bothPassSame
                ? constants.CONSTRAINTS_SAME_PASS
                : constants.CONSTRAINTS_SATISFIED}
            </Typography>
            <ul style={{ margin: '0px 0px 0px 16px', padding: '0px' }}>
              {passwordConstraintsError.specialCharacter && (
                <Typography {...constraintTypographyProps}>
                  <li>{constants.CONSTRAINT1}</li>
                </Typography>
              )}
              {passwordConstraintsError.aNumber && (
                <Typography {...constraintTypographyProps}>
                  <li>{constants.CONSTRAINT2}</li>
                </Typography>
              )}
              {passwordConstraintsError.upperCaseLetter && (
                <Typography {...constraintTypographyProps}>
                  <li>{constants.CONSTRAINT3}</li>
                </Typography>
              )}
              {passwordConstraintsError.lowerCaseLetter && (
                <Typography {...constraintTypographyProps}>
                  <li>{constants.CONSTRAINT4}</li>
                </Typography>
              )}
              {passwordConstraintsError.minCharacters && (
                <Typography {...constraintTypographyProps}>
                  <li>{constants.CONSTRAINT5}</li>
                </Typography>
              )}
            </ul>
          </Grid>
          <Grid style={{ margin: '28px 0px' }}>
            <div className={classes.firstItem}>
              <Checkbox
                className={classes.checkbox}
                color="primary"
                onChange={() => setTerms(!terms)}
                checked={terms}
                disableRipple={true}
                icon={
                  <CheckBoxOutlineBlankOutlinedIcon
                    style={{
                      color: patientAccessTheme.palette.checkbox?.border1,
                    }}
                  />
                }
              />
              <Typography
                textColor={patientAccessTheme.palette.text.highemphasis}
                fontSize="12px"
                lineHeight="20px"
                fontType={'normal'}
                margin="0px 0px 0px 12px"
              >
                {'I agree to the '}
              </Typography>
              <Typography
                fontSize="12px"
                fontType="semiBold"
                lineHeight="20px"
                textColor={patientAccessTheme.palette.primary[500]}
                className={classes.text}
                cursor="pointer"
                onClick={() => setTermsModal(true)}
              >
                {' Terms & Conditions'}
              </Typography>
            </div>
            <Button
              label={
                <Typography
                  textColor={patientAccessTheme.palette.status.white}
                  fontSize="16px"
                  lineHeight="24px"
                  fontType="semiBold"
                >
                  {isCreatePassword
                    ? constants.CREATE_PASSWORD
                    : constants.RESET_PASSWORD}
                </Typography>
              }
              variant="contained"
              width="372px"
              height="44px"
              color="primary"
              borderRadius="4px"
              onClick={handleSubmit}
              disabled={!allConstraintsSatisfied || !bothPassSame || !terms}
            />
          </Grid>
        </Grid>
      </div>
      <TermsModal open={termsModal} handleClose={() => setTermsModal(false)} />
    </>
  );
};

export default ResetPasswordOrganism;
